/**
 * The invitee module tour's groups (design: ob-invitee.jsx INV_GROUPS),
 * mapped onto People's app ids and the shell's own routes.
 *
 * Only modules the org actually has enabled (the validate response's
 * `modules`) are shown, and a group with none of them is skipped. The
 * design's "Sync" (official correspondence) has no WeCo counterpart and is
 * left out.
 */
import type { LucideIcon } from "lucide-react";
import { getRouteById } from "../../../routes";
import { ObIcons } from "../kit";
import type { InvitationModule } from "../kit";

export type TourGroupId = "essentials" | "communication" | "work" | "files";

export interface TourModuleDef {
  /** People `AppDefinition.app_id`. */
  appId: string;
  /** i18n key under `onboarding.invitee.tour.mod.<key>`. */
  key: string;
  icon: LucideIcon;
  /** Shell route id (routes.ts) the "Open …" button goes to. */
  routeId: string;
  /** English defaults (the design's copy). */
  name: string;
  desc: string;
}

export interface TourGroupDef {
  id: TourGroupId;
  name: string;
  modules: ReadonlyArray<TourModuleDef>;
}

export const TOUR_GROUPS: ReadonlyArray<TourGroupDef> = [
  {
    id: "essentials",
    name: "Essentials",
    modules: [
      { appId: "assistant", key: "mochi", icon: ObIcons.sparkle, routeId: "dashboard", name: "Mochi", desc: "Ask anything about your work. Mochi drafts, summarizes and plans for you." },
      { appId: "email", key: "email", icon: ObIcons["envelope-simple"], routeId: "email", name: "Email", desc: "Your company inbox, with shared mailboxes for your department." },
      { appId: "calendar", key: "planner", icon: ObIcons["calendar-blank"], routeId: "planner", name: "Planner", desc: "Meetings, focus time and team calendars in one view." },
      { appId: "contacts", key: "contacts", icon: ObIcons["address-book"], routeId: "contacts", name: "Contacts", desc: "Everyone in {{org}}, plus the people you work with outside it." },
    ],
  },
  {
    id: "communication",
    name: "Communication",
    modules: [
      { appId: "chat", key: "chat", icon: ObIcons["chats-circle"], routeId: "chat", name: "Chat", desc: "Direct messages and channels for quick questions." },
      { appId: "video", key: "call", icon: ObIcons["video-camera"], routeId: "call", name: "Call", desc: "Voice and video calls with anyone in the workspace." },
    ],
  },
  {
    id: "work",
    name: "Work",
    modules: [
      { appId: "tasks", key: "tasks", icon: ObIcons["check-square"], routeId: "tasks", name: "Tasks", desc: "Hubs, squads and sprints. See what is due today." },
      { appId: "homework", key: "homework", icon: ObIcons["house-line"], routeId: "homework", name: "HomeWork", desc: "Your desk, office and day plan for working from anywhere." },
    ],
  },
  {
    id: "files",
    name: "Files & content",
    modules: [
      { appId: "drive", key: "files", icon: ObIcons["folder-simple"], routeId: "files", name: "Files", desc: "Department folders and documents shared with you." },
      { appId: "docs", key: "write", icon: ObIcons["pen-nib"], routeId: "write", name: "Write", desc: "Documents and reports, drafted with Mochi." },
      { appId: "present", key: "present", icon: ObIcons.presentation, routeId: "present", name: "Present", desc: "Slide decks built from your company templates." },
      { appId: "grid", key: "grid", icon: ObIcons.table, routeId: "grid", name: "Grid", desc: "Spreadsheets, tables and charts." },
    ],
  },
];

/** The tour groups for an org's enabled modules, empty groups dropped. */
export function tourGroupsFor(enabled: ReadonlyArray<InvitationModule>): TourGroupDef[] {
  const ids = new Set(enabled.map((m) => m.app_id));
  return TOUR_GROUPS.map((g) => ({ ...g, modules: g.modules.filter((m) => ids.has(m.appId)) })).filter(
    (g) => g.modules.length > 0,
  );
}

/** The shell path a tour module opens. */
export function tourModulePath(mod: Pick<TourModuleDef, "routeId">): string {
  return getRouteById(mod.routeId)?.path ?? "/";
}
